import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Trophy, 
  Zap, 
  Search, 
  BarChart3,
  Star,
  Crown,
  Target,
  TrendingUp,
  Users,
  RefreshCw
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import PokemonVariantDisplay from './PokemonVariantDisplay';
import { MovesetPanel, TypeBadge, MetricDisplay, RankingRow, PageContainer, SectionHeader } from './shared';
import { getPokemonImage, createPokemonImageErrorHandler, useImageMapReady } from '../utils/pokemonImageResolver';
import { normalizePokemonData, normalizeMoveName } from '../utils/pokemonDataUtils';
import { translateMoveName, useGameI18nReady } from '../utils/gameI18n';
import { getApiBaseUrl } from '../utils/apiUrl';

// Component for Pokemon image - using single source of truth resolver
const PokemonImageWithFallback = ({ pokemon, pokemonName, pokemonId, pokeIndex, variant = 'default' }) => {
  const imageUrl = getPokemonImage(pokemon || pokemonId, variant);
  const handleImageError = createPokemonImageErrorHandler(pokemon || pokemonId, { variant });
  
  if (!imageUrl || imageUrl.includes('000.png')) {
    return (
      <div className="flex-shrink-0 w-20 h-20 sm:w-24 sm:h-24 bg-gray-200 rounded-xl flex items-center justify-center border-2 border-gray-300">
        <span className="text-lg font-bold text-gray-600">{pokeIndex + 1}</span>
      </div>
    );
  }
  
  return (
    <div className="flex-shrink-0 w-20 h-20 sm:w-24 sm:h-24 bg-white rounded-xl flex items-center justify-center border-3 border-gray-300 shadow-sm overflow-hidden">
      <img
        src={imageUrl}
        alt={pokemonName || `Pokemon ${pokeIndex + 1}`}
        className="w-full h-full object-contain p-2"
        onError={handleImageError}
      />
    </div>
  );
};

const GoBattleLeague = () => {
  const { language, t } = useLanguage();
  const i18nReady = useGameI18nReady(); // Re-render when game i18n loads (for move translations)
  const imageMapReady = useImageMapReady(); // Re-render when image map loads
  const [leagues, setLeagues] = useState([]);
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [rankings, setRankings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [sortBy, setSortBy] = useState('score');
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20
  });
  const [stats, setStats] = useState(null);
  const [recommendedTeams, setRecommendedTeams] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [activeTab, setActiveTab] = useState('rankings'); // 'teams' or 'rankings' - default to rankings
  const [teamFilter, setTeamFilter] = useState('all'); // 'all', 'top', 'popular'

  useEffect(() => {
    fetchLeagues();
  }, []);

  const fetchLeagues = async () => {
    try {
      // Use schedule/public endpoint which filters to Core + Active only
      const response = await fetch(`${getApiBaseUrl()}/api/leagues/schedule/public`);
      if (!response.ok) {
        // Fallback to old endpoint if schedule endpoint doesn't exist
        const fallbackResponse = await fetch(`${getApiBaseUrl()}/api/gbl/leagues`);
        if (!fallbackResponse.ok) {
          throw new Error(`HTTP error! status: ${fallbackResponse.status}`);
        }
        const fallbackData = await fallbackResponse.json();
        if (fallbackData.success) {
          // Filter for core + active leagues on client side
          const now = new Date();
          now.setHours(0, 0, 0, 0);
          const filtered = fallbackData.data.filter(league => {
            // Show core leagues always
            if (league.is_core) return true;
            // Show active leagues
            if (league.start_date && league.end_date) {
              const start = new Date(league.start_date);
              const end = new Date(league.end_date);
              return now >= start && now <= end;
            }
            return false;
          });
          setLeagues(filtered);
          if (filtered.length > 0) {
            setSelectedLeague(filtered[0]);
          }
        }
      } else {
        const data = await response.json();
        if (data.success && data.data) {
          console.log('✅ [GBL] Loaded leagues:', data.data.length);
          setLeagues(data.data);
          if (data.data.length > 0) {
            setSelectedLeague(data.data[0]);
          }
        } else {
          console.warn('⚠️ [GBL] No leagues data in response:', data);
          setLeagues([]);
        }
      }
    } catch (error) {
      console.error('Error fetching leagues:', error);
      // Set empty leagues to prevent further errors
      setLeagues([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchRankings = useCallback(async (page = 1, append = false) => {
    if (!selectedLeague) return;
    
    if (page === 1) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    
    try {
      const params = new URLSearchParams({
        page: page,
        limit: pagination.itemsPerPage,
        search: searchTerm,
        type: typeFilter,
        sort: sortBy
      });
      
      const response = await fetch(`${getApiBaseUrl()}/api/gbl/rankings/${selectedLeague.id}?${params}`);
      const data = await response.json();
      
      if (data.success) {
        // Normalize Pokemon data to ensure consistent field names
        const normalizedData = (data.data || []).map(pokemon => normalizePokemonData(pokemon, language));
        
        if (append) {
          setRankings(prev => [...prev, ...normalizedData]);
        } else {
          setRankings(normalizedData);
        }
        
        setPagination(prev => ({
          ...prev,
          currentPage: data.pagination.currentPage,
          totalPages: data.pagination.totalPages,
          totalItems: data.pagination.totalItems,
          hasNextPage: data.pagination.hasNextPage,
          hasPrevPage: data.pagination.hasPrevPage
        }));
        
        setHasMore(data.pagination.hasNextPage);
      }
    } catch (error) {
      console.error('Error fetching rankings:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [selectedLeague, pagination.itemsPerPage, searchTerm, typeFilter, sortBy]);

  const loadMore = useCallback(() => {
    if (hasMore && !loadingMore && !loading) {
      fetchRankings(pagination.currentPage + 1, true);
    }
  }, [hasMore, loadingMore, loading, pagination.currentPage, fetchRankings]);

  const fetchStats = useCallback(async () => {
    if (!selectedLeague) return;
    
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/gbl/stats/${selectedLeague.id}`);
      const data = await response.json();
      if (data.success) {
        setStats(data.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [selectedLeague]);

  const fetchRecommendedTeams = useCallback(async () => {
    if (!selectedLeague) return;
    
    setLoadingTeams(true);
    try {
      const params = new URLSearchParams({
        page: '1',
        limit: '1000', // เพิ่ม limit เป็น 1000 เพื่อแสดงทีมทั้งหมด
        league: selectedLeague.id.toString(),
        sortBy: 'weight',
        sortOrder: 'desc'
      });
      
      const response = await fetch(`${getApiBaseUrl()}/api/teams?${params}`);
      const data = await response.json();
      
      if (data.success) {
        const teams = data.data.teams || data.data || [];
        // Store raw team data - normalization happens at render time with current language
        setRecommendedTeams(teams);
      }
    } catch (error) {
      console.error('Error fetching recommended teams:', error);
      setRecommendedTeams([]);
    } finally {
      setLoadingTeams(false);
    }
  }, [selectedLeague]);

  useEffect(() => {
    if (selectedLeague) {
      fetchRankings(1, false);
      fetchStats();
      fetchRecommendedTeams();
    }
  }, [selectedLeague, fetchStats, fetchRecommendedTeams]);

  useEffect(() => {
    if (selectedLeague) {
      fetchRankings(1, false);
    }
  }, [searchTerm, typeFilter, sortBy]);

  // Infinite scroll effect
  useEffect(() => {
    const handleScroll = () => {
      if (window.innerHeight + document.documentElement.scrollTop >= document.documentElement.offsetHeight - 1000) {
        loadMore();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loadMore]);

  // Smart search with suggestions
  const handleSearchChange = (value) => {
    setSearchTerm(value);
    
    if (value.length > 0) {
      // Generate suggestions based on current rankings
      const suggestions = rankings
        .filter(pokemon => 
          pokemon.pokemon_name.toLowerCase().includes(value.toLowerCase()) ||
          pokemon.pokemon_types.some(type => type.toLowerCase().includes(value.toLowerCase()))
        )
        .slice(0, 5)
        .map(pokemon => ({
          name: pokemon.pokemon_name,
          types: pokemon.pokemon_types,
          score: pokemon.score
        }));
      
      setSearchSuggestions(suggestions);
      setShowSuggestions(true);
    } else {
      setSearchSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchTerm(suggestion.name);
    setShowSuggestions(false);
  };

  const getRankIcon = (rank) => {
    const numRank = parseInt(rank);
    if (numRank <= 10) return <Crown className="w-4 h-4 text-yellow-500" />;
    if (numRank <= 50) return <Star className="w-4 h-4 text-blue-500" />;
    return <Target className="w-4 h-4 text-gray-500" />;
  };

  if (loading && !rankings.length) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">{t('gbl.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageContainer maxWidth="7xl" padding={true} className="py-8">
        {/* Section Header */}
        <SectionHeader
          title={t('gbl.title')}
        />
        {/* League Selection - Compact Tab Design */}
        <div className="mb-6 border-b border-gray-200">
          <div className="flex flex-wrap gap-2 -mb-px">
            {leagues.map((league) => (
              <button
                key={league.id}
                onClick={() => setSelectedLeague(league)}
                className={`group relative flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-all duration-150 ${
                  selectedLeague?.id === league.id
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-600 hover:text-gray-900 hover:border-b-2 hover:border-gray-300'
                }`}
              >
                {/* League Icon - Small */}
                {league.icon ? (
                  <img 
                    src={`/images/leagues/${league.icon}`}
                    alt={league.name}
                    className="w-4 h-4 object-contain opacity-80"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      if (e.target.nextSibling) e.target.nextSibling.style.display = 'block';
                    }}
                  />
                ) : (
                  <Trophy className="w-4 h-4 opacity-70" />
                )}
                <span className="whitespace-nowrap">
                  {language === 'th' ? (league.name_th || league.name) : league.name}
                </span>
                {league.ranking_count !== undefined && league.ranking_count > 0 && (
                  <span className={`ml-1 px-1.5 py-0.5 rounded-full text-xs font-medium ${
                    selectedLeague?.id === league.id
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {league.ranking_count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {selectedLeague && (
          <>
            {/* Search and Filters */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-lg border border-gray-200 p-4 mb-6"
            >
              {/* Mobile Layout */}
              <div className="lg:hidden space-y-4">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-6 h-6" />
                  <input
                    type="text"
                    placeholder={t('gbl.searchPlaceholder')}
                    value={searchTerm}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    onFocus={() => searchSuggestions.length > 0 && setShowSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                    className="w-full pl-12 pr-4 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base font-medium transition-all duration-150"
                  />
                  
                  {/* Search Suggestions */}
                  {showSuggestions && searchSuggestions.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
                      {searchSuggestions.map((suggestion, index) => (
                        <div
                          key={index}
                          onClick={() => handleSuggestionClick(suggestion)}
                          className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="font-medium text-gray-900">{suggestion.name}</div>
                              <div className="text-sm text-gray-500">
                                {suggestion.types.join(', ')} • Score: {suggestion.score}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <select
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                    className="px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base font-medium transition-all duration-150"
                  >
                    <option value="">{t('gbl.allTypes')}</option>
                    <option value="Fire">Fire</option>
                    <option value="Water">Water</option>
                    <option value="Grass">Grass</option>
                    <option value="Electric">Electric</option>
                    <option value="Psychic">Psychic</option>
                    <option value="Ice">Ice</option>
                    <option value="Dragon">Dragon</option>
                    <option value="Dark">Dark</option>
                    <option value="Fairy">Fairy</option>
                    <option value="Fighting">Fighting</option>
                    <option value="Poison">Poison</option>
                    <option value="Ground">Ground</option>
                    <option value="Flying">Flying</option>
                    <option value="Bug">Bug</option>
                    <option value="Rock">Rock</option>
                    <option value="Ghost">Ghost</option>
                    <option value="Steel">Steel</option>
                    <option value="Normal">Normal</option>
                  </select>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base font-medium transition-all duration-150"
                  >
                    <option value="score">{t('gbl.sortByScore')}</option>
                    <option value="rank">{t('gbl.sortByRank')}</option>
                  </select>
                </div>
              </div>
              
              {/* Desktop Layout */}
              <div className="hidden lg:flex flex-col lg:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-6 h-6" />
                    <input
                      type="text"
                      placeholder={t('gbl.searchPlaceholder')}
                      value={searchTerm}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      onFocus={() => searchSuggestions.length > 0 && setShowSuggestions(true)}
                      onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                      className="w-full pl-12 pr-4 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base font-medium"
                    />
                    
                    {/* Search Suggestions */}
                    {showSuggestions && searchSuggestions.length > 0 && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
                        {searchSuggestions.map((suggestion, index) => (
                          <div
                            key={index}
                            onClick={() => handleSuggestionClick(suggestion)}
                            className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="font-medium text-gray-900">{suggestion.name}</div>
                                <div className="text-sm text-gray-500">
                                  {suggestion.types.join(', ')} • Score: {suggestion.score}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex gap-4">
                  <select
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                    className="px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base font-semibold"
                  >
                    <option value="">{t('gbl.allTypes')}</option>
                    <option value="Fire">Fire</option>
                    <option value="Water">Water</option>
                    <option value="Grass">Grass</option>
                    <option value="Electric">Electric</option>
                    <option value="Psychic">Psychic</option>
                    <option value="Ice">Ice</option>
                    <option value="Dragon">Dragon</option>
                    <option value="Dark">Dark</option>
                    <option value="Fairy">Fairy</option>
                    <option value="Fighting">Fighting</option>
                    <option value="Poison">Poison</option>
                    <option value="Ground">Ground</option>
                    <option value="Flying">Flying</option>
                    <option value="Bug">Bug</option>
                    <option value="Rock">Rock</option>
                    <option value="Ghost">Ghost</option>
                    <option value="Steel">Steel</option>
                    <option value="Normal">Normal</option>
                  </select>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="px-5 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base font-semibold"
                  >
                    <option value="score">{t('gbl.sortByScore')}</option>
                    <option value="rank">{t('gbl.sortByRank')}</option>
                  </select>
                </div>
              </div>
            </motion.div>

            {/* Tabs for Mobile & Desktop - Switch between Rankings and Teams */}
            <div className="mb-6">
              <div className="border-b-2 border-gray-200">
                <nav className="flex space-x-2" aria-label="Tabs">
                  <button
                    onClick={() => setActiveTab('rankings')}
                    className={`flex-1 sm:flex-none px-6 py-4 text-base sm:text-lg font-semibold transition-all duration-200 border-b-3 ${
                      activeTab === 'rankings'
                        ? 'border-blue-600 text-blue-600 bg-blue-50'
                        : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-center space-x-2">
                      <Trophy className="w-5 h-5 sm:w-6 sm:h-6" />
                      <span className="whitespace-nowrap">{t('gbl.pokemonRankings')}</span>
                      {rankings.length > 0 && (
                        <span className={`px-3 py-1 text-sm font-bold rounded-full ${
                          activeTab === 'rankings' ? 'bg-blue-200 text-blue-900' : 'bg-gray-200 text-gray-700'
                        }`}>
                          {pagination.totalItems}
                        </span>
                      )}
                    </div>
                  </button>
                  <button
                    onClick={() => setActiveTab('teams')}
                    className={`flex-1 sm:flex-none px-6 py-4 text-base sm:text-lg font-semibold transition-all duration-200 border-b-3 ${
                      activeTab === 'teams'
                        ? 'border-blue-600 text-blue-600 bg-blue-50'
                        : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-center space-x-2">
                      <Users className="w-5 h-5 sm:w-6 sm:h-6" />
                      <span className="whitespace-nowrap">{t('gbl.recommendedTeams')}</span>
                      {recommendedTeams.length > 0 && (
                        <span className={`px-3 py-1 text-sm font-bold rounded-full ${
                          activeTab === 'teams' ? 'bg-blue-200 text-blue-900' : 'bg-gray-200 text-gray-700'
                        }`}>
                          {recommendedTeams.length}
                        </span>
                      )}
                    </div>
                  </button>
                </nav>
              </div>
            </div>

            {/* Rankings Section - Only show when tab is active (shown first) */}
            {activeTab === 'rankings' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                {/* Mobile Cards - Redesigned with Moveset Priority */}
                <div className="lg:hidden space-y-3">
                  {rankings.length > 0 ? rankings.map((pokemon, index) => (
                    <motion.div
                      key={pokemon.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-sm transition-all duration-150 hover:border-gray-300"
                    >
                      {/* Pokemon Identity Section */}
                      <div className="px-4 pt-4 pb-3">
                        <div className="flex items-center space-x-3 mb-2">
                          <div className="relative flex-shrink-0">
                            <PokemonVariantDisplay
                              pokemon={{
                                ...pokemon,
                                pokemon_id: pokemon.pokemon_id || pokemon.Pokemon_ID || pokemon.id,
                                image_url: getPokemonImage(pokemon, 'default')
                              }}
                              showBadges={true}
                              showCssClasses={true}
                              className="w-16 h-16"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-lg font-bold text-gray-900 truncate mb-1">
                              {pokemon.pokemon_name || (language === 'th' ? pokemon.Pokemon_Name_TH : pokemon.Pokemon_Name_EN) || pokemon.name || 'Unknown Pokemon'}
                            </h3>
                            <div className="flex flex-wrap gap-1.5">
                              {pokemon.pokemon_types ? pokemon.pokemon_types.map((type, idx) => (
                                <TypeBadge key={idx} type={type} size="default" />
                              )) : (
                                <>
                                  {pokemon.type1 && <TypeBadge type={pokemon.type1} size="default" />}
                                  {pokemon.type2 && pokemon.type2 !== 'none' && <TypeBadge type={pokemon.type2} size="default" />}
                                </>
                              )}
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <div className="text-xs text-gray-500 mb-0.5">
                              {t('gbl.rank')}
                            </div>
                            <div className="flex items-center space-x-1">
                              {getRankIcon(pokemon.rank || pokemon.rank_position)}
                              <span className="text-sm font-semibold text-gray-700">
                                #{pokemon.rank || pokemon.rank_position}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Moveset Panel - HERO SECTION */}
                      <div className="px-4 pb-4">
                        <MovesetPanel
                          fastMove={pokemon.fast_move}
                          chargedMoves={[pokemon.charged_move_1, pokemon.charged_move_2].filter(Boolean)}
                          variant="default"
                          language={language}
                        />
                        
                        {/* Score - Secondary */}
                        <div className="mt-3 flex items-center justify-end">
                          <MetricDisplay
                            label={t('gbl.score')}
                            value={parseFloat(pokemon.score || 0).toFixed(1)}
                            variant="default"
                            align="right"
                            language={language}
                          />
                        </div>
                      </div>
                    </motion.div>
                  )) : (
                    <div className="text-center py-12">
                      <Trophy className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500 text-lg mb-2">
                        {t('gbl.noRankings')}
                      </p>
                      <p className="text-gray-400 text-sm">
                        {t('gbl.noRankingsHint')}
                      </p>
                    </div>
                  )}
                </div>

                {/* Desktop Table - Redesigned with Moveset Priority */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, delay: 0.15 }}
                  className="hidden lg:block bg-white rounded-lg border border-gray-200 overflow-hidden mt-4"
                >
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gradient-to-r from-slate-50 to-blue-50 border-b border-gray-200">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            {t('gbl.pokemon')}
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider" style={{ width: '40%' }}>
                            {t('moves.title')}
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            {t('gbl.rank')}
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            {t('gbl.score')}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-100">
                        {rankings.map((pokemon, index) => (
                          <RankingRow
                            key={pokemon.id}
                            pokemon={pokemon}
                            index={index}
                            language={language}
                            getRankIcon={getRankIcon}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                </motion.div>

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                  <div className="mt-8">
                    {/* Mobile Pagination */}
                    <div className="lg:hidden">
                      <div className="text-center text-base font-medium text-gray-700 mb-5">
                        {t('gbl.showing')} {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1} {t('gbl.to')} {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)} {t('gbl.of')} {pagination.totalItems} {t('gbl.results')}
                      </div>
                      <div className="flex justify-center space-x-4">
                        <button
                          onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }))}
                          disabled={!pagination.hasPrevPage}
                          className="px-8 py-4 text-base font-bold text-gray-800 bg-white border-2 border-gray-400 rounded-xl hover:bg-gray-100 hover:border-gray-600 disabled:opacity-40 disabled:cursor-not-allowed shadow-md active:scale-95 transition-all"
                        >
                          {`← ${t('detail.previous')}`}
                        </button>
                        <div className="flex items-center px-6 py-4 bg-blue-100 text-blue-900 rounded-xl font-bold text-lg shadow-md">
                          {pagination.currentPage} / {pagination.totalPages}
                        </div>
                        <button
                          onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }))}
                          disabled={!pagination.hasNextPage}
                          className="px-8 py-4 text-base font-bold text-gray-800 bg-white border-2 border-gray-400 rounded-xl hover:bg-gray-100 hover:border-gray-600 disabled:opacity-40 disabled:cursor-not-allowed shadow-md active:scale-95 transition-all"
                        >
                          {`${t('detail.next')} →`}
                        </button>
                      </div>
                    </div>
                    
                    {/* Desktop Pagination */}
                    <div className="hidden lg:flex items-center justify-between">
                      <div className="text-base font-medium text-gray-700">
                        {t('gbl.showing')} {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1} {t('gbl.to')} {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)} {t('gbl.of')} {pagination.totalItems} {t('gbl.results')}
                      </div>
                      <div className="flex space-x-3">
                        <button
                          onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }))}
                          disabled={!pagination.hasPrevPage}
                          className="px-6 py-3 text-base font-semibold text-gray-800 bg-white border-2 border-gray-400 rounded-lg hover:bg-gray-100 hover:border-gray-600 disabled:opacity-40 disabled:cursor-not-allowed shadow-md active:scale-95 transition-all"
                        >
                          {`← ${t('detail.previous')}`}
                        </button>
                        <button
                          onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }))}
                          disabled={!pagination.hasNextPage}
                          className="px-6 py-3 text-base font-semibold text-gray-800 bg-white border-2 border-gray-400 rounded-lg hover:bg-gray-100 hover:border-gray-600 disabled:opacity-40 disabled:cursor-not-allowed shadow-md active:scale-95 transition-all"
                        >
                          {`${t('detail.next')} →`}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Loading More Indicator for Rankings */}
                {loadingMore && (
                  <div className="flex justify-center items-center py-8">
                    <div className="flex items-center space-x-2 text-gray-600">
                      <RefreshCw className="w-5 h-5 animate-spin" />
                      <span>{t('gbl.loadingMore')}</span>
                    </div>
                  </div>
                )}
                
                {/* End of Results for Rankings */}
                {!hasMore && rankings.length > 0 && (
                  <div className="flex justify-center items-center py-8">
                    <div className="text-gray-500 text-sm">
                      {`${t('gbl.showingAll')} (${rankings.length} ${t('gbl.results')})`}
                    </div>
                  </div>
                )}
              </motion.div>
            )}

            {/* Recommended Teams Section - Only show when tab is active */}
            {activeTab === 'teams' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="mb-8"
              >
                {loadingTeams ? (
                  <div className="flex items-center justify-center py-12">
                    <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
                    <span className="ml-2 text-gray-600">{t('gbl.loadingTeams')}</span>
                  </div>
                ) : recommendedTeams.length > 0 ? (
                  <>
                    {/* Team Filter Tabs */}
                    <div className="mb-6 flex flex-wrap gap-3 justify-center">
                      <button
                        onClick={() => setTeamFilter('all')}
                        className={`px-6 py-3 rounded-lg font-bold transition-all duration-200 ${
                          language === 'th' ? 'text-base sm:text-lg' : 'text-sm sm:text-base'
                        } ${
                          teamFilter === 'all'
                            ? 'bg-blue-600 text-white shadow-lg scale-105'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        {t('gbl.all')} ({recommendedTeams.length})
                      </button>
                      <button
                        onClick={() => setTeamFilter('top')}
                        className={`px-6 py-3 rounded-lg font-bold transition-all duration-200 flex items-center space-x-2 ${
                          language === 'th' ? 'text-base sm:text-lg' : 'text-sm sm:text-base'
                        } ${
                          teamFilter === 'top'
                            ? 'bg-gradient-to-r from-yellow-500 to-yellow-400 text-white shadow-lg scale-105'
                            : 'bg-yellow-100 text-yellow-900 hover:bg-yellow-200 border-2 border-yellow-300'
                        }`}
                      >
                        <Crown className="w-5 h-5" />
                        <span>{t('gbl.topTeams')} ({recommendedTeams.filter(tm => tm.recommendation_tier === 'TOP').length})</span>
                      </button>
                      <button
                        onClick={() => setTeamFilter('popular')}
                        className={`px-6 py-3 rounded-lg font-bold transition-all duration-200 ${
                          language === 'th' ? 'text-base sm:text-lg' : 'text-sm sm:text-base'
                        } ${
                          teamFilter === 'popular'
                            ? 'bg-gradient-to-r from-blue-500 to-blue-400 text-white shadow-lg scale-105'
                            : 'bg-blue-100 text-blue-900 hover:bg-blue-200 border-2 border-blue-300'
                        }`}
                      >
                        {t('gbl.popularTeams')} ({recommendedTeams.filter(tm => tm.recommendation_tier === 'POPULAR').length})
                      </button>
                    </div>

                    {/* Filtered Teams Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
                      {recommendedTeams
                        .filter(team => {
                          if (teamFilter === 'top') return team.recommendation_tier === 'TOP';
                          if (teamFilter === 'popular') return team.recommendation_tier === 'POPULAR';
                          return true; // 'all'
                        })
                        .map((team, index) => (
                      <motion.div
                        key={team.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-lg transition-all duration-200"
                      >
                        {/* Team Header */}
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1 min-w-0">
                            {/* Team Title - ใหญ่ขึ้น อ่านง่าย */}
                            <h3 className={`font-bold mb-3 line-clamp-2 text-gray-900 ${
                              language === 'th' 
                                ? 'text-xl sm:text-2xl leading-relaxed' 
                                : 'text-lg sm:text-xl'
                            }`}>
                              {(language === 'th' 
                                ? (team.team_name_th || team.team_name) 
                                : (team.team_name || team.team_name_th)) 
                              || (team.team_number ? `Team #${team.team_number}` : `Team #${team.id}`)}
                            </h3>
                            <div className="flex flex-wrap items-center gap-3">
                              {/* Badge - ใหญ่ขึ้น */}
                              <span className={`px-4 py-2 rounded-full flex items-center space-x-2 shadow-sm ${
                                team.recommendation_tier === 'TOP'
                                  ? 'bg-gradient-to-r from-yellow-100 to-yellow-50 text-yellow-900 border-2 border-yellow-300'
                                  : 'bg-gradient-to-r from-blue-100 to-blue-50 text-blue-900 border-2 border-blue-300'
                              }`}>
                                {team.recommendation_tier === 'TOP' && <Crown className="w-5 h-5 flex-shrink-0" />}
                                <span className={`font-bold whitespace-nowrap ${
                                  language === 'th' ? 'text-base sm:text-lg' : 'text-sm sm:text-base'
                                }`}>
                                  {team.recommendation_tier === 'TOP' ? t('gbl.topTeams') : t('gbl.popularTeams')}
                                </span>
                              </span>
                              {team.weight !== null && team.weight !== undefined && (
                                <span className={`text-gray-700 whitespace-nowrap font-semibold ${
                                  language === 'th' ? 'text-base sm:text-lg' : 'text-sm sm:text-base'
                                }`}>
                                  {t('gbl.score')}: {typeof team.weight === 'number' ? team.weight.toFixed(1) : parseFloat(team.weight || 0).toFixed(1)}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Pokemon List */}
                        {team.pokemon && team.pokemon.length > 0 && (
                          <div className="space-y-3 sm:space-y-4 mt-6">
                            {team.pokemon.map((poke, pokeIndex) => {
                              // Normalize Pokemon data
                              const normalizedPoke = normalizePokemonData(poke, language);
                              // Extract Pokemon ID from multiple possible fields
                              const pokemonId = normalizedPoke?.pokemon_id || 
                                               normalizedPoke?.id || 
                                               normalizedPoke?.Pokemon_ID ||
                                               normalizedPoke?.speciesId ||
                                               normalizedPoke?.base_id ||
                                               poke?.pokemon_id ||
                                               poke?.id ||
                                               poke?.Pokemon_ID ||
                                               poke?.speciesId ||
                                               null;
                              // Use mapped name from backend (should already be correct from database mapping)
                              const pokemonName = normalizedPoke.pokemon_name || 
                                                  (language === 'th' ? normalizedPoke.Pokemon_Name_TH : normalizedPoke.Pokemon_Name_EN) ||
                                                  (language === 'th' ? normalizedPoke.name_th : normalizedPoke.name) || 
                                                  normalizedPoke.Pokemon_Name_EN ||
                                                  normalizedPoke.name ||
                                                  `Pokemon ${pokeIndex + 1}`;
                              // Keep English move names for type detection, translateMoveName handles language
                              const fastMove = normalizeMoveName(normalizedPoke.fast_move, 'en');
                              const chargedMove1 = normalizeMoveName(normalizedPoke.charged_move_1, 'en');
                              const chargedMove2 = normalizeMoveName(normalizedPoke.charged_move_2, 'en');
                              
                              // Use pokemonImageResolver - SINGLE SOURCE OF TRUTH
                              return (
                                <div
                                  key={pokeIndex}
                                  className="flex items-center space-x-4 p-4 bg-gradient-to-r from-gray-50 to-gray-100/50 rounded-xl hover:from-blue-50 hover:to-blue-100/50 transition-all duration-200 border border-gray-200 hover:border-blue-300 hover:shadow-md"
                                >
                                  {/* Pokemon Image - ใหญ่ขึ้นเพื่อความชัดเจน */}
                                  <PokemonImageWithFallback
                                    pokemon={normalizedPoke || poke}
                                    pokemonName={pokemonName}
                                    pokemonId={pokemonId || normalizedPoke?.pokemon_id || normalizedPoke?.id || normalizedPoke?.Pokemon_ID || poke?.speciesId || poke?.pokemon_id || poke?.id}
                                    pokeIndex={pokeIndex}
                                    variant="default"
                                  />
                                  
                                  {/* Pokemon Info - ตัวหนังสือใหญ่ขึ้น อ่านง่ายขึ้น */}
                                  <div className="flex-1 min-w-0">
                                    {/* Pokemon Name - Thai text ใหญ่เพื่ออ่านง่าย */}
                                    <div className={`font-bold mb-2 truncate ${
                                      language === 'th' 
                                        ? 'text-xl sm:text-2xl text-gray-900 leading-tight' 
                                        : 'text-lg sm:text-xl text-gray-900'
                                    }`}>
                                      {pokemonName}
                                    </div>
                                    
                                    {/* Moves */}
                                    <div className="space-y-1.5">
                                      {fastMove && (
                                        <div className="flex items-center gap-2">
                                          <span className={`font-semibold text-gray-700 whitespace-nowrap ${
                                            language === 'th' ? 'text-base' : 'text-sm'
                                          }`}>
                                            {t('moves.fast')}:
                                          </span>
                                          <span className={`text-gray-800 truncate ${
                                            language === 'th' ? 'text-base' : 'text-sm'
                                          }`}>
                                            {translateMoveName(fastMove, language)}
                                          </span>
                                        </div>
                                      )}
                                      {(chargedMove1 || chargedMove2) && (
                                        <div className="flex items-start gap-2">
                                          <span className={`font-semibold text-gray-700 whitespace-nowrap mt-0.5 ${
                                            language === 'th' ? 'text-base' : 'text-sm'
                                          }`}>
                                            {t('moves.charged')}:
                                          </span>
                                          <span className={`text-gray-800 leading-relaxed ${
                                            language === 'th' ? 'text-base leading-loose' : 'text-sm'
                                          }`}>
                                            {[chargedMove1, chargedMove2].filter(Boolean).map(m => translateMoveName(m, language)).join(', ')}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Team Notes/Description - ตัวหนังสือใหญ่ขึ้น */}
                        {team.notes && (
                          <p className={`mt-4 text-gray-600 line-clamp-2 leading-relaxed ${
                            language === 'th' ? 'text-base' : 'text-sm'
                          }`}>
                            {team.notes}
                          </p>
                        )}
                        </motion.div>
                      ))}
                    </div>

                    {/* Show message if filtered results are empty */}
                    {recommendedTeams.filter(team => {
                      if (teamFilter === 'top') return team.recommendation_tier === 'TOP';
                      if (teamFilter === 'popular') return team.recommendation_tier === 'POPULAR';
                      return true;
                    }).length === 0 && (
                      <div className="text-center py-12">
                        <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500 text-lg mb-2">
                          {teamFilter === 'top' 
                            ? `${t('gbl.noTeams')} (${t('gbl.topTeams')})`
                            : teamFilter === 'popular' 
                              ? `${t('gbl.noTeams')} (${t('gbl.popularTeams')})`
                              : t('gbl.noTeams')}
                        </p>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-12">
                    <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 text-lg mb-2">
                      {t('gbl.noTeams')}
                    </p>
                    <p className="text-gray-400 text-sm">
                      {t('gbl.noTeamsHint')}
                    </p>
                  </div>
                )}
              </motion.div>
            )}
          </>
        )}
      </PageContainer>
    </div>
  );
  };
  
export default GoBattleLeague;